import "./Filter.css";
import type {
	EventFilterValue,
	EventFilterView,
	GameEventFilterUtilityProps,
	GamePageResponse,
	GamePageLazyResponse,
	GPEventResponse,
} from "../../../types";
import {
	FilterFlagsKey,
	FilterFlagsType,
	ProcessingLevelEnum,
	TeamFilterKey,
	VideoSidebar,
	type AttachModeTarget,
} from "../../../types";
import { useState } from "react";
import { useVideoPlayer } from "../../../Contexts/contexts";
import clsx from "clsx";
import { Badge } from "ui-kit";
import { handlePlayEvent } from "../../../functions";
import { _ } from "i18n";
import { Keys } from "i18n/locales/types";
import { FilterStorage } from "./FilterStorage";
import { useGameStore } from "../../../store";
import { useAttachStore, AttachModeSidebarView } from "../../../attach-store";
import { buildLockedFilterValues } from "../../../attach-mode";
import { AttachModeLockBadge } from "../AttachModeLockBadge";

interface FilterProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	gameEventFilters?: GameEventFilterUtilityProps;
}

function shouldHideAttachFilter(filterKey: string, target: AttachModeTarget | null): boolean {
	if (target === null) {
		return false;
	}

	return filterKey === TeamFilterKey.Home || filterKey === TeamFilterKey.Away || filterKey === "divider.1";
}

export function Filter({ gameData, gameDataLazy, gameEventFilters }: FilterProps) {
	const [openedPanes, setOpenedPanes] = useState<string[]>([]);

	const { filterValues, setFilterValues, setFilteredFilterValues, setSidebar, eventFilter, setEvents } = useGameStore();
	const { target: attachTarget, setSidebarView: setAttachSidebarView } = useAttachStore();
	const isAttachMode = attachTarget !== null;

	const { playerRef } = useVideoPlayer();
	const isLightTagged = gameData.game.processingLevel === ProcessingLevelEnum.Limited;

	const applyFilterValues = (nextFilterValues: EventFilterValue[]) => {
		setFilterValues(nextFilterValues);
		if (isAttachMode) {
			setFilteredFilterValues(nextFilterValues);
		}
	};

	const handleOnToggleFilter = (key: string, value: string, isUnique = false) => {
		applyFilterValues(eventFilter.mutateFilterValues(filterValues, key, value, isUnique));
	};

	const handleOnClearFilter = () => {
		if (attachTarget !== null) {
			applyFilterValues(buildLockedFilterValues(eventFilter, attachTarget));
			return;
		}

		setFilteredFilterValues([]);
		setFilterValues([]);
	};

	const handleOnTogglePane = (key: string) => {
		const opened = [...openedPanes];
		const index = opened.indexOf(key);

		if (index > -1) {
			opened.splice(index, 1);
		} else {
			opened.push(key);
		}

		setOpenedPanes(opened);
	};

	const handleOnPlayFilteredEvents = () => {
		setEvents(eventFilter.filter(filterValues), filterValues);
		setFilteredFilterValues(filterValues);

		if (isAttachMode) {
			setAttachSidebarView(AttachModeSidebarView.EventList);
			setSidebar(VideoSidebar.AttachMode);
		} else {
			setSidebar(VideoSidebar.EventList);
		}

		handlePlayEvent(playerRef);
	};

	const isOpen = (key: string): boolean => {
		return openedPanes.includes(key);
	};

	const filteredEvents = eventFilter.filter(filterValues);

	return (
		<div className={clsx("eventFilter-container", { "eventFilter-container--attach": isAttachMode })}>
			{isAttachMode && attachTarget !== null && (
				<div className="filter-attachLock">
					<AttachModeLockBadge target={attachTarget} />
				</div>
			)}
			<div className="filter-header">
				<FilterStorage isAllowed={gameData.permissions.showEvents} gameEventFilters={gameEventFilters} />
				<Badge
					label={_("event.filter.button.clearFilter")}
					variant="default"
					outline={true}
					block={true}
					size="sm"
					disabled={filterValues.length === 0}
					onClick={handleOnClearFilter}
				/>
			</div>
			<div className="filter-body">
				{eventFilter.filterViews.map((r: EventFilterView) => {
					if (r.key === "hasComments" || shouldHideAttachFilter(r.key, attachTarget)) {
						return null;
					}

					return (
						<div className="filter-group" key={r.key}>
							<FilterElement
								filter={r}
								filterValues={filterValues}
								events={gameDataLazy?.events || []}
								level={1}
								handleToggleFilter={handleOnToggleFilter}
								handleTogglePane={handleOnTogglePane}
								isOpen={isOpen}
								isLightTagged={isLightTagged}
							/>
						</div>
					);
				})}
			</div>
			<div className="filter-footer">
				<Badge
					label={_("event.filter.button.play", { count: String(filteredEvents.length) })}
					variant="primary"
					icon="play"
					block={true}
					onClick={handleOnPlayFilteredEvents}
					disabled={filteredEvents.length === 0}
				/>
			</div>
		</div>
	);
}

interface FilterElementProps {
	filter: EventFilterView;
	filterValues: EventFilterValue[];
	events: GPEventResponse[];
	handleToggleFilter: (key: string, value: string, isUnique: boolean) => void;
	handleTogglePane: (key: string) => void;
	isOpen: (key: string) => boolean;
	level: number;
	isLightTagged: boolean;
}

function shouldSkipFilter(filter: EventFilterView, isLightTagged: boolean) {
	if (!isLightTagged) {
		return false;
	}
	if ([
		FilterFlagsKey.OffensiveShotInsidePa,
		FilterFlagsKey.DefensiveRecovery,
		FilterFlagsKey.DistributionPassReceived,
	].includes(filter.key as FilterFlagsKey)) {
		return true;
	}

	return false;
}

function FilterElement({ filter, filterValues, events, level, handleToggleFilter, handleTogglePane, isOpen, isLightTagged }: FilterElementProps) {
	const { eventFilter } = useGameStore();

	if (shouldSkipFilter(filter, isLightTagged)) {
		return null;
	}

	const isActive = (key: string, value: string) => {
		const filterValue = filterValues.find((v) => v.key === key);

		return !!filterValue && filterValue.value.includes(value as never);
	};

	const showCount = filter.key !== "highlight.item";
	const eventCount = eventFilter.eventsCount(filter.key, [filter.value], filterValues);
	const hasNestedValues = eventFilter.hasNestedFilterValues(filter.key, filterValues);

	const isSelfChecked = isActive(filter.key, filter.value || filter.key);
	const hasFilterFlags = [FilterFlagsType.MultipleFlags, FilterFlagsType.SingleFlags].includes(filter.type as FilterFlagsType) ||
		(filter.nested && filter.nested.some((nestedFilter) => {
			return [FilterFlagsType.MultipleFlags, FilterFlagsType.SingleFlags].includes(nestedFilter.type as FilterFlagsType);
		}));

	let showCheckIcon = true;
	let showMinusIcon = false;

	const getNestedCounts = (parentFilter: EventFilterView): { checked: number; total: number } => {
		let checked = 0;
		let total = 0;

		for (const nested of parentFilter.nested ?? []) {
			if (nested == null) {
				continue;
			}

			// Keep parent checkbox state consistent with what we actually render.
			// If a nested filter is hidden (returns null), it must not contribute to totals.
			if (shouldSkipFilter(nested, isLightTagged)) {
				continue;
			}

			// If the nested filter has options, count at the option level (same unit as "total").
			if (nested?.options?.size != null && nested.options.size > 0) {
				const options = Array.from(nested.options as Map<string, string>);
				total += options.length;
				checked += options.filter(([key]) => isActive(nested.value || nested.key, key)).length;
				continue;
			}

			// Otherwise count this nested filter as a single selectable item.
			total += 1;
			if (isActive(nested.key, nested.value || nested.key)) checked += 1;
		}

		return { checked, total };
	};

	const { checked: nestedCheckedCount, total: nestedTotalCount } = getNestedCounts(filter);
	const isFullyNestedSelected = !hasFilterFlags && nestedTotalCount > 0 && nestedCheckedCount === nestedTotalCount;
	const isChecked = isSelfChecked || isFullyNestedSelected;

	// Show an indeterminate/minus icon only for genuine partial selection (some but not all nested selected).
	if (!hasFilterFlags && nestedTotalCount > 0 && nestedCheckedCount > 0 && nestedCheckedCount < nestedTotalCount) {
		showMinusIcon = true;
		showCheckIcon = false;
	}

	return (
		<>
			{filter.type === "divider" && <div className="filter-divider" />}
			{filter.type === "checkbox" && (
				<>
					<div className={`filter--handle filter--handle--${level}`}>
						<div
							onClick={() => {
								handleToggleFilter(filter.key, filter.value || filter.key, true);
							}}
							className={`filter--checkbox filter--checkbox--${level}`}
						>
							<span
								className={clsx("filter--checkbox--checkbox", {
									"is-checked": isChecked || showMinusIcon,
								})}
							>
								{showCheckIcon && <span className="icon icon-check icon-10" />}
								{showMinusIcon && <span className="icon icon-minus icon-10" />}
							</span>
							{_(filter.label as keyof Keys)}{" "}
							<span className="filter__count">({eventCount})</span>{" "}
						</div>
						{filter.nested &&
							filter.nested.length > 0 &&
							(eventCount > 0 || isOpen(filter.key) || hasNestedValues) && (
							<div
								className={clsx("filter--collapse", {
									"is-open": isOpen(filter.key),
								})}
								onClick={() => handleTogglePane(filter.key)}
							>
								<span className="icon icon-chevron-down-grey icon-24" />
							</div>
						)}
					</div>
					{filter.nested && filter.nested.length > 0 && (
						<div
							className={clsx("filter--collapsible", {
								"is-open": isOpen(filter.key),
							})}
						>
							{filter.nested.map((nestedFilter: EventFilterView) => (
								<div className={clsx("filter--nested", `filter--nested--${level}`)} key={nestedFilter.key}>
									<FilterElement
										filter={nestedFilter}
										filterValues={filterValues}
										events={events}
										level={level + 1}
										handleToggleFilter={handleToggleFilter}
										handleTogglePane={handleTogglePane}
										isOpen={isOpen}
										isLightTagged={isLightTagged}
									/>
								</div>
							))}
						</div>
					)}
				</>
			)}
			{filter.type === "checkboxList" && (
				<div className="filter--checkbox-list">
					{filter?.options &&
						Array.from(filter.options as Map<string, string>).map(([key, label]) => (
							<div
								key={`o-${key}`}
								onClick={() => handleToggleFilter(filter.value || filter.key, key, false)}
								className={clsx("filter--checkbox-list__item", `filter--nested--${level}`)}
							>
								<span
									className={clsx("filter--checkbox--checkbox", {
										"is-checked": isActive(filter.value || filter.key, key),
									})}
								>
									<span className="icon icon-check icon-10" />
								</span>
								{_(label as keyof Keys)}{" "}
								{showCount && <span className="filter__count">({eventFilter.eventsCount(filter.key, [key], filterValues)})</span>}
							</div>
						))}
				</div>
			)}
			{(filter.type === FilterFlagsType.MultipleFlags || filter.type === FilterFlagsType.SingleFlags) && (() => {
				const optionEntries = filter?.options ? Array.from(filter.options as Map<string, string>) : [];

				// Compute expensive counts once per option and reuse for both visibility + rendering.
				let hasAnyEvents = false;
				const optionItems = optionEntries.map(([key, label]) => {
					const eventsCount = eventFilter.eventsCount(filter.key, [key], filterValues);
					if (eventsCount > 0) hasAnyEvents = true;
					return { key, label, eventsCount };
				});

				if (!hasAnyEvents) {
					return null;
				}

				return (
					<div className="filter-flags">
						<div className="filter-flags__label">{_(filter.label as keyof Keys)}</div>
						{optionItems.map(({ key, label, eventsCount }) => {
							const isSelected = isActive(filter.value || filter.key, key);

							return (
								<Badge
									key={key}
									label={_(label as keyof Keys)}
									size="xs"
									disabled={eventsCount === 0}
									clickableOnDisabled={eventsCount === 0 && isSelected}
									className="filter-flags__flag"
									outline={!isSelected}
									variant={isSelected ? "info" : "default"}
									onClick={() => handleToggleFilter(filter.value || filter.key, key, filter.type === FilterFlagsType.SingleFlags)}
								/>
							);
						})}
					</div>
				);
			})()}
		</>
	);
}
